import React, { FC, useState, createContext, useEffect, useContext, useLayoutEffect } from "react";
import { Prompt, RouteComponentProps } from "react-router-dom";
import Header from "./components/header/Header";
import TwoColAside from "../../common/components/layouts/TwoColAside";
import PlotBox from "./components/plot/PlotBox";
import BladesContainer from "./components/blade/BladesContainer";
import useProjectStore from "common/store/ProjectStore";
import useSettingsStore from "common/store/SettingsStore";
import useNotificationStore from "common/store/NotificationStore";
import { LanguageContext } from "common/contexts/LanguageContext";
import LoadingSpinnerBackdrop from "common/components/progress/LoadingSpinner";
import { Box } from "@mui/material";
import ScaleDialog, { ScaleStep } from "./ScaleDialog";
import { useAuthContext } from "common/contexts/AuthContext";
import { useAuth0 } from '@auth0/auth0-react';

export const SharedProjectContext = createContext({
  scaleSet: false,
  setScaleSet: (b: boolean) => {},
  multiSelect: false,
  setMultiSelect: (b: boolean) => {},
  copy: () => {},
  setCopy: (value: () => {}) => {},
  paste: () => {},
  setPaste: (value: () => {}) => {},
  deleteSelected: () => {},
  setDeleteSelected: (value: () => {}) => {},
  projectSaved: false,
  setProjectSaved: (b: boolean) => {},
  calculationsToggle: true,
  setCalculationsToggle: (b: boolean) => {},
  isBusyBackgroundActive: false,
  setIsBusyBackgroundActive: (b: boolean) => {},
  networkDeleted: false,
  setNetworkDeleted: (b: boolean) => {},
  bladesViewAvailable: false,
  setBladesViewAvailable: (b: boolean) => {},
  networkCheck: false,
  setNetworkCheck: (b: boolean) => {},
  networkHealth: false,
  setNetworkHealth: (b: boolean) => {},
  triggerCalculations: false,
  setTriggerCalculations: (b: boolean) => {},
});

interface IMatchParams {
  projectID: string;
}
interface IProps extends RouteComponentProps<IMatchParams> {}

const Project: FC<IProps> = ({ match }) => {
  const [scaleSet, setScaleSet] = useState(false);
  const [multiSelect, setMultiSelect] = useState(false);
  const [copy, setCopy] = useState<() => void>(() => {});
  const [paste, setPaste] = useState<() => void>(() => {});
  const [deleteSelected, setDeleteSelected] = useState<() => void>(() => {});
  const [projectSaved, setProjectSaved] = useState(false);
  const [calculationsToggle, setCalculationsToggle] = useState(true);
  const [isScaleDialogOpen, setIsScaleDialogOpen] = useState<boolean>(false);
  const [minBladeHeight, setMinBladeHeight] = useState<number>(0);
  const [maxBladeHeight, setMaxBladeHeight] = useState<number>(800);
  const [isBusyBackgroundActive, setIsBusyBackgroundActive] = useState(false);
  const [networkDeleted, setNetworkDeleted] = useState(false);
  const [bladesViewAvailable, setBladesViewAvailable] = useState(false);
  const [networkCheck, setNetworkCheck] = useState(false);
  const [networkHealth, setNetworkHealth] = useState(false);
  const [triggerCalculations, setTriggerCalculations] = useState(false);

  const { projectID: urlProjectID } = match.params;
  const { settings } = useSettingsStore();
  const { projectID, fetchProject, loading, scalingFactor, isDirty, rowVersion } = useProjectStore();
  const { enqueueNotification } = useNotificationStore();
  const { getString } = useContext(LanguageContext);
    const { getAccessToken, isAuthenticatedUser } = useAuthContext();

  useEffect(() => {
      if (+urlProjectID > 0 && isAuthenticatedUser && !loading && (+urlProjectID !== projectID || rowVersion === null)) {
      (async () => {
        const response = await fetchProject(settings.apiBaseUrl, getAccessToken, +urlProjectID);
        if (!response.succeeded) {
          enqueueNotification("load_project_failure", getString("msg_proj_refetch_failed"), "error");
          return;
        }
      })();
    }
  }, [settings, fetchProject, projectID, urlProjectID, loading, getString, enqueueNotification, getAccessToken, isAuthenticatedUser, rowVersion]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isDirty) {
        event.preventDefault();
        // For Chrome, setting event.returnValue to an empty string is necessary
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isDirty]);

  useLayoutEffect(() => {
    if (!scaleSet && (scalingFactor === null || scalingFactor === 0) && +urlProjectID === projectID) {
      setIsScaleDialogOpen(true);
    }
  }, [projectID, urlProjectID, scaleSet, scalingFactor]);

  // This useEffect needs to execute every render so no dependencies
  // eslint-disable-next-line
  useEffect(() => {
    var el = document.getElementById("imageContainer");
    if (el) {
      var minHeight = scalingFactor ? el.clientHeight - 20 : el.clientHeight;
      setMinBladeHeight(minHeight);
    }
    var maxHeight = scalingFactor ? window.innerHeight - 266 - 20 : window.innerHeight - 266;
    setMaxBladeHeight(maxHeight);

    const handleResize = () => {
      var el = document.getElementById("imageContainer");
      if (el) {
        setMinBladeHeight(el.clientHeight);
      }

      setMaxBladeHeight(window.innerHeight - 266);
    }

    window.addEventListener("resize", handleResize, false);

    return () => {
      window.removeEventListener("resize", handleResize, false);
    }
  })

  useEffect(() => {
    setIsBusyBackgroundActive(isScaleDialogOpen);
  }, [isScaleDialogOpen])

  const handleScaleDialogCancel = () => {
    setIsScaleDialogOpen(false);
  };

  const handleScaleDialogConfirm = () => {
    setIsScaleDialogOpen(false);
    setScaleSet(!scaleSet);
  };

  return (
    <>
      {loading && <LoadingSpinnerBackdrop />}
      <SharedProjectContext.Provider
        value={{
          scaleSet,
          setScaleSet,
          multiSelect,
          setMultiSelect,
          copy,
          setCopy,
          paste,
          setPaste,
          deleteSelected,
          setDeleteSelected,
          projectSaved,
          setProjectSaved,
          calculationsToggle,
          setCalculationsToggle,
          isBusyBackgroundActive,
          setIsBusyBackgroundActive,
          networkDeleted,
          setNetworkDeleted,
          bladesViewAvailable,
          setBladesViewAvailable,
          networkCheck,
          setNetworkCheck,
          networkHealth,
          setNetworkHealth,
          triggerCalculations,
          setTriggerCalculations,
        }}
      >
        <TwoColAside
          top={<Header />}
          aside={
            <Box sx={{ width: "auto", maxHeight: {maxBladeHeight} }}>
              <BladesContainer minBladeHeight={minBladeHeight} maxBladeHeight={maxBladeHeight} />
            </Box>
          }
          fullWidth={false}
        >
          <Box sx={{ ml: "-24px", flexGrow: 1, minWidth: "300px", overflow: "hidden" }} id="imageContainer">
            <PlotBox />
          </Box>
        </TwoColAside>
      </SharedProjectContext.Provider>
      {!scaleSet && (scalingFactor === null || scalingFactor === 0) && (
        <ScaleDialog
          stepNumber={ScaleStep.Explanation}
          isOpen={isScaleDialogOpen}
          handleClose={handleScaleDialogCancel}
          handleConfirm={handleScaleDialogConfirm}
        />
      )}
      <Prompt when={isDirty} message={getString("msg_unsaved_work")} />
    </>
  );
};

export default Project;
